import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Search } from 'lucide-react';
import SEO from '../components/SEO';

const NotFound = () => {
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-20 bg-gradient-to-br from-white to-gray-50 dark:from-slate-900 dark:to-slate-800 transition-colors duration-500">
      <SEO 
        title="404 - Page Not Found" 
        description="The page you are looking for doesn't exist. Please navigate back to home or check our services."
      />
      
      <div className="max-w-3xl w-full text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative inline-block mb-12"
        >
          <h1 className="text-[12rem] font-black text-slate-100 dark:text-slate-800 leading-none select-none tracking-tighter">
            404
          </h1>
          <div className="absolute inset-0 flex items-center justify-center pt-8">
            <motion.div 
              animate={{ 
                rotate: [0, 5, -5, 0],
                y: [0, -10, 0]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 4,
                ease: "easeInOut"
              }}
              className="p-8 bg-white dark:bg-slate-700 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-600 backdrop-blur-sm"
            >
              <Search className="w-20 h-20 text-orange-600 dark:text-orange-500" />
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight">
            Oops! Page Not Found
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-300 mb-10 max-w-lg mx-auto leading-relaxed">
            The page you're searching for might have been moved, deleted, or never existed. 
            Don't worry, our expert painters can help you find your way back.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-8 py-4 bg-orange-600 hover:bg-orange-700 text-white rounded-full font-bold shadow-lg shadow-orange-600/20 transition-all duration-300"
              >
                <Home size={20} />
                Back to Home
              </motion.button>
            </Link>
            
            <button 
              onClick={() => window.history.back()}
              className="flex items-center gap-2 px-8 py-4 bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-full font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all duration-300 shadow-sm"
            >
              <ArrowLeft size={20} />
              Previous Page
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-16 pt-12 border-t border-slate-200 dark:border-slate-800"
        >
          <p className="text-slate-500 dark:text-slate-400 font-medium mb-6 uppercase tracking-widest text-sm">
            Quick Links
          </p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4">
            <Link to="/services/interior" className="text-slate-600 dark:text-slate-300 hover:text-orange-600 transition-colors">Interior Painting</Link>
            <Link to="/services/exterior" className="text-slate-600 dark:text-slate-300 hover:text-orange-600 transition-colors">Exterior Painting</Link>
            <Link to="/contact" className="text-slate-600 dark:text-slate-300 hover:text-orange-600 transition-colors">Get A Quote</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound;
